import { useEffect, useMemo, useState } from "react";
import { useSearch, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Clock, Loader2, RefreshCw } from "lucide-react";

import {
  FilterStateSchema,
  SPECIES,
  type DecisionDto,
  type FilterState,
  type InteractionCandidate,
  type SpeciesKey,
} from "@shared/schema";
import { api } from "@/lib/api";
import { getDeviceId } from "@/lib/deviceId";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  ActiveFilterChips,
  ResultsSidebar,
  type SidebarContext,
} from "@/components/ResultsSidebar";
import { InteractionTable } from "@/components/InteractionTable";
import { InteractionDetailDrawer } from "@/components/InteractionDetailDrawer";
import { LabPriorityPanel } from "@/components/LabPriorityPanel";
import { ProteinInfoCard } from "@/components/ProteinInfoCard";
import { cn } from "@/lib/utils";

type ResultsTab = "interactions" | "lab" | "proteins" | "decisions";

function parseQuery(search: string) {
  const params = new URLSearchParams(search);
  const genes =
    params
      .get("genes")
      ?.split(",")
      .map((s) => s.trim())
      .filter(Boolean) ?? [];
  const species = (params.get("species") as SpeciesKey | null) ?? "human";
  const tissue = params.get("tissue") ?? undefined;
  const cellType = params.get("cellType") ?? undefined;
  const diseaseMode = params.get("diseaseMode") ?? undefined;
  const repairState = params.get("repairState") ?? undefined;
  return { genes, species, tissue, cellType, diseaseMode, repairState };
}

function applyFilters(list: InteractionCandidate[], f: FilterState): InteractionCandidate[] {
  const lower = f.partnerGeneSearch.trim().toLowerCase();
  return list.filter((c) => {
    if ((c.scores.combined ?? 0) < f.confidenceMin) return false;
    if (f.evidenceTiers.length > 0 && !f.evidenceTiers.includes(c.evidenceTier)) return false;
    if (f.experimentalOnly && !(c.scores.experimental != null && c.scores.experimental >= 0.15)) return false;
    if (f.diseaseRelevantOnly && c.diseaseAssociations.length === 0) return false;
    if (f.highPriorityOnly && (c.labPriority?.score ?? 0) < 0.6) return false;
    if (lower && !c.partnerGene.toLowerCase().includes(lower)) return false;
    return true;
  });
}

export function ResultsPage() {
  const search = useSearch();
  const [, setLocation] = useLocation();
  const parsed = useMemo(() => parseQuery(search), [search]);

  const [context, setContext] = useState<SidebarContext>({
    tissue: parsed.tissue,
    cellType: parsed.cellType,
    diseaseMode: parsed.diseaseMode,
    repairState: parsed.repairState,
  });
  const [filters, setFilters] = useState<FilterState>(() =>
    FilterStateSchema.parse({ confidenceMin: 0 }),
  );
  const [tab, setTab] = useState<ResultsTab>("interactions");
  const [selected, setSelected] = useState<InteractionCandidate | null>(null);
  const [exportBusy, setExportBusy] = useState<"csv" | "json" | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<number | null>(null);

  useEffect(() => {
    setContext({
      tissue: parsed.tissue,
      cellType: parsed.cellType,
      diseaseMode: parsed.diseaseMode,
      repairState: parsed.repairState,
    });
  }, [parsed.tissue, parsed.cellType, parsed.diseaseMode, parsed.repairState]);

  const analysisQ = useQuery({
    queryKey: ["analysis", parsed.genes, parsed.species, context],
    queryFn: () =>
      api.runAnalysis({
        genes: parsed.genes,
        species: parsed.species,
        context,
      }),
    enabled: parsed.genes.length > 0,
  });

  const queryRunId = analysisQ.data?.queryRunId;
  const qc = useQueryClient();
  const runDecisionsQ = useQuery({
    queryKey: ["run-decisions", queryRunId],
    queryFn: () =>
      api.currentDecisionsForRun(getDeviceId(), queryRunId!),
    enabled: !!queryRunId,
  });

  const filtered = useMemo(
    () => (analysisQ.data ? applyFilters(analysisQ.data.interactions, filters) : []),
    [analysisQ.data, filters],
  );

  const refreshMut = useMutation({
    mutationFn: () =>
      api.runAnalysis({
        genes: parsed.genes,
        species: parsed.species,
        context,
        refresh: true,
      }),
    onSuccess: (data) => {
      qc.setQueryData(["analysis", parsed.genes, parsed.species, context], data);
      setLastRefreshAt(Date.now());
    },
  });

  const downloadBlob = async (kind: "csv" | "json") => {
    setExportBusy(kind);
    try {
      const res = await fetch(kind === "csv" ? "/api/exports/csv" : "/api/exports/json", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          genes: parsed.genes,
          species: parsed.species,
          context,
        }),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `academ-${analysisQ.data?.queryRunId ?? "results"}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExportBusy(null);
    }
  };

  const savePanel = async () => {
    const name = window.prompt(
      "Panel name",
      `${parsed.genes.slice(0, 3).join(", ")}${parsed.genes.length > 3 ? " +" + (parsed.genes.length - 3) : ""}`,
    );
    if (!name) return;
    await api.createPanel({
      name,
      genes: parsed.genes,
      species: parsed.species,
      notes: context.tissue ? `tissue: ${context.tissue}` : undefined,
    });
  };

  const goToCompare = () => {
    if (parsed.genes.length < 2) return;
    const params = new URLSearchParams({
      genes: parsed.genes.join(","),
      species: parsed.species,
    });
    if (context.tissue) params.set("tissue", context.tissue);
    if (context.cellType) params.set("cellType", context.cellType);
    if (context.diseaseMode) params.set("diseaseMode", context.diseaseMode);
    if (context.repairState) params.set("repairState", context.repairState);
    setLocation(`/comparison?${params.toString()}`);
  };

  if (parsed.genes.length === 0) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center">
        <h1 className="font-display text-xl font-semibold">No query</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Go back to the Query page to enter genes or UniProt IDs.
        </p>
        <Button variant="ghost" onClick={() => setLocation("/")}>
          <ArrowLeft className="h-4 w-4" /> Back to search
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-56px)] w-full">
      <div className="w-64 shrink-0">
        <ResultsSidebar
          sourceGenes={parsed.genes}
          species={parsed.species}
          taxId={SPECIES[parsed.species].taxId}
          filters={filters}
          onFiltersChange={setFilters}
          context={context}
          onContextChange={setContext}
          onNewSearch={() => setLocation("/")}
          onExportCsv={() => downloadBlob("csv")}
          onExportJson={() => downloadBlob("json")}
          onSavePanel={savePanel}
          onCompare={goToCompare}
          onRefresh={() => refreshMut.mutate()}
          exportBusy={exportBusy}
          refreshBusy={refreshMut.isPending}
        />
      </div>

      <section className="min-w-0 flex-1 px-5 py-4">
        <ResultsHeader
          total={analysisQ.data?.interactions.length ?? 0}
          shown={filtered.length}
          meta={analysisQ.data?.meta}
          refreshedAt={lastRefreshAt}
        />

        <Tabs
          value={tab}
          onChange={setTab}
          counts={{
            interactions: filtered.length,
            lab: filtered.filter((i) => (i.labPriority?.score ?? 0) >= 0.4).length,
            proteins: Object.keys(analysisQ.data?.proteinInfo ?? {}).length,
            decisions: Object.keys(runDecisionsQ.data ?? {}).length,
          }}
        />

        {analysisQ.isPending && (
          <div className="mt-6 flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Resolving external sources…
          </div>
        )}

        {analysisQ.isError && (
          <div className="mt-6 rounded-lg border border-destructive/40 bg-destructive/5 p-4">
            <p className="font-semibold text-destructive">Analysis failed</p>
            <p className="mt-1 text-sm">{(analysisQ.error as Error)?.message ?? "Unknown error"}</p>
            <Button onClick={() => analysisQ.refetch()} className="mt-3">Retry</Button>
          </div>
        )}

        {analysisQ.data && (
          <div className="mt-3 space-y-3">
            <SourceHealthBanner health={analysisQ.data.sourceHealth} />

            <ActiveFilterChips filters={filters} onChange={setFilters} />

            {tab === "interactions" && (
              <InteractionTable
                rows={filtered}
                onSelect={setSelected}
                selectedId={selected?.id}
                decisionsByInteraction={runDecisionsQ.data ?? undefined}
              />
            )}
            {tab === "lab" && (
              <LabPriorityPanel interactions={filtered} onSelect={setSelected} />
            )}
            {tab === "proteins" && (
              <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {parsed.genes
                  .map((g) => analysisQ.data.proteinInfo[g])
                  .filter((p): p is NonNullable<typeof p> => !!p)
                  .map((info) => (
                    <ProteinInfoCard key={info.geneName + info.uniprotId} info={info} />
                  ))}
              </section>
            )}
            {tab === "decisions" && (
              <RunDecisionsTab
                decisions={Object.values(runDecisionsQ.data ?? {})}
                interactions={analysisQ.data.interactions}
                onSelect={setSelected}
              />
            )}
          </div>
        )}
      </section>

      <InteractionDetailDrawer
        row={selected}
        proteinInfo={analysisQ.data?.proteinInfo ?? {}}
        partnerDetails={analysisQ.data?.partnerDetails ?? {}}
        queryRunId={queryRunId}
        existingDecision={selected ? runDecisionsQ.data?.[selected.id] : undefined}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/* ───────────────────────── Header strip + tabs ───────────────────────── */

function ResultsHeader({
  total,
  shown,
  meta,
  refreshedAt,
}: {
  total: number;
  shown: number;
  meta?: { appVersion: string; kbVersion: string; scoringConfigVersion: string };
  refreshedAt: number | null;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-2">
      <h1 className="font-display text-lg font-semibold">
        {shown === total ? `${total} interactions` : `${shown} of ${total} interactions`}
      </h1>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>STRING v12</span>
        <span>·</span>
        <span>UniProt 2025</span>
        <span>·</span>
        <span>Open Targets Platform</span>
        <span>·</span>
        <span>AlphaFold DB</span>
        {meta && (
          <>
            <span>·</span>
            <span className="font-mono">
              app {meta.appVersion} · kb {meta.kbVersion} · scoring {meta.scoringConfigVersion}
            </span>
          </>
        )}
        {refreshedAt && (
          <span className="inline-flex items-center gap-1 text-accent">
            <RefreshCw className="h-3 w-3" /> refreshed {new Date(refreshedAt).toLocaleTimeString()}
          </span>
        )}
      </div>
    </div>
  );
}

function Tabs({
  value,
  onChange,
  counts,
}: {
  value: ResultsTab;
  onChange: (v: ResultsTab) => void;
  counts: Record<ResultsTab, number>;
}) {
  const TABS: Array<{ id: ResultsTab; label: string }> = [
    { id: "interactions", label: "Interactions" },
    { id: "lab", label: "Lab Priority" },
    { id: "proteins", label: "Query Proteins" },
    { id: "decisions", label: "Decisions" },
  ];
  return (
    <nav className="mt-1 flex items-center gap-1 border-b border-border/60 text-sm">
      {TABS.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className={cn(
            "inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors",
            value === t.id
              ? "border-accent text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground",
          )}
        >
          {t.label}
          <span
            className={cn(
              "inline-flex h-5 items-center rounded-full px-1.5 text-[11px] font-semibold",
              value === t.id ? "bg-accent/20 text-accent-foreground/80" : "bg-muted text-muted-foreground",
            )}
          >
            {counts[t.id]}
          </span>
        </button>
      ))}
    </nav>
  );
}

/* ───────────────────────── Source health banner ───────────────────────── */

function SourceHealthBanner({ health }: { health: { source: string; status: string; message?: string }[] }) {
  const degraded = health.filter((h) => h.status !== "ok");
  if (degraded.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-[hsl(38_90%_60%/0.5)] bg-[hsl(44_94%_95%)] px-3 py-2 text-xs dark:bg-[hsl(38_42%_16%)]">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(28_80%_40%)]" />
      <div className="space-y-1">
        <p className="font-semibold">Some sources degraded — results are partial but still ranked.</p>
        <div className="flex flex-wrap gap-1.5">
          {degraded.map((h) => (
            <Badge key={h.source} className="border-border">
              {h.source}: {h.status}
            </Badge>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Decisions tab ───────────────────────── */

function RunDecisionsTab({
  decisions,
  interactions,
  onSelect,
}: {
  decisions: DecisionDto[];
  interactions: InteractionCandidate[];
  onSelect: (r: InteractionCandidate) => void;
}) {
  if (decisions.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <Clock className="mx-auto mb-2 h-5 w-5" />
        <p className="font-medium text-foreground">No decisions recorded for this run yet.</p>
        <p className="mt-1">
          Open any interaction and mark it as <em>Validate now</em>, <em>Maybe later</em>, or{" "}
          <em>Reject</em>. Your choices persist to the Decision Log.
        </p>
      </div>
    );
  }
  const byId = new Map(interactions.map((i) => [i.id, i] as const));
  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card text-sm">
      {decisions.map((d) => {
        const row = byId.get(d.interactionId);
        return (
          <li key={d.id} className="flex items-center gap-3 px-3 py-2">
            <span
              className={cn(
                "priority-dot",
                d.action === "validate_now" && "bg-priority-high",
                d.action === "maybe_later" && "bg-priority-mid",
                d.action === "reject" && "bg-destructive",
              )}
            />
            <button
              type="button"
              className="font-mono text-[13px] font-medium text-foreground hover:underline"
              onClick={() => row && onSelect(row)}
            >
              {d.sourceGene} — {d.partnerGene}
            </button>
            <span className="text-xs text-muted-foreground">{d.action.replace("_", " ")}</span>
            {d.reason && (
              <span className="truncate text-xs text-muted-foreground">· {d.reason}</span>
            )}
            <span className="ml-auto text-[11px] text-muted-foreground">
              {new Date(d.createdAt).toLocaleString()}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
